import { useId, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import { useSignInAgain } from "../hooks/useSignInAgain";

interface FeatureCheckFailedCardProps {
  icon: LucideIcon;
  title: string;
  message: string;
  retrying: boolean;
  retryFailed: boolean;
  waitingForNetwork: boolean;
  announceWaiting: boolean;
  sessionEnded: boolean;
  onRetry?: () => void;
  restoreFocusRef: RefObject<boolean>;
}

let lastArmedRestore: RefObject<boolean> | null = null;

function armRestore(ref: RefObject<boolean>) {
  if (lastArmedRestore !== null && lastArmedRestore !== ref) lastArmedRestore.current = false;
  lastArmedRestore = ref;
  ref.current = true;
}

export function FeatureCheckFailedCard({
  icon: Icon,
  title,
  message,
  retrying,
  retryFailed,
  waitingForNetwork,
  announceWaiting,
  sessionEnded,
  onRetry,
  restoreFocusRef,
}: FeatureCheckFailedCardProps) {
  const { t } = useTranslation();
  const signInAgain = useSignInAgain("/account");
  const messageId = useId();
  const waitingId = useId();
  const announceSessionEnded = sessionEnded && retryFailed;
  const showWaiting = waitingForNetwork && !sessionEnded;

  return (
    <div className="rounded-xl border border-border bg-surface p-6">
      <h3 className="flex items-center gap-2 text-base font-semibold text-heading">
        <Icon size={18} />
        {title}
      </h3>
      <p
        key={announceSessionEnded ? "message-announced" : "message-quiet"}
        id={messageId}
        role={announceSessionEnded ? "alert" : undefined}
        className="mt-1 text-xs text-muted"
      >
        {message}
      </p>
      {showWaiting && (
        <p
          key={announceWaiting ? "announced" : "quiet"}
          id={waitingId}
          role={announceWaiting ? "alert" : undefined}
          className="mt-1 text-xs text-muted"
        >
          {t("common.waitingForNetwork")}
        </p>
      )}
      {sessionEnded ? (
        <button
          type="button"
          onClick={signInAgain}
          aria-describedby={messageId}
          className="mt-3 rounded-lg border border-border bg-surface px-3.5 py-2 text-xs font-medium text-heading transition-colors hover:border-border-light"
        >
          {t("account.staleSession.action")}
        </button>
      ) : (
        onRetry && (
          <button
            type="button"
            onClick={() => {
              if (retrying) return;
              armRestore(restoreFocusRef);
              onRetry();
            }}
            aria-describedby={showWaiting ? `${messageId} ${waitingId}` : messageId}
            aria-disabled={retrying || undefined}
            aria-busy={(retrying && !showWaiting) || undefined}
            className="mt-3 rounded-lg border border-border bg-surface px-3.5 py-2 text-xs font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {t("common.retry")}
          </button>
        )
      )}
      {onRetry && retryFailed && !retrying && !sessionEnded && (
        <p role="alert" className="mt-2 text-xs text-muted">
          {t("account.codesAtRisk.retryFailed")}
        </p>
      )}
    </div>
  );
}
